/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'

import { Grid } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Icon from '@mui/material/Icon'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query'
import {
	Cell,
	Row,
	TableInstance,
	useAsyncDebounce,
	useGlobalFilter,
	usePagination,
	useSortBy,
	useTable,
} from 'react-table'

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox'
import MDInput from 'components/MDInput'
import MDPagination from 'components/MDPagination'
import MDTypography from 'components/MDTypography'
import DataTableBodyCell from 'modules/Tables/DataTable/DataTableBodyCell'
import DataTableHeadCell from 'modules/Tables/DataTable/DataTableHeadCell'
import { Column, FilterListValue, SpecialColumn, TabularData } from 'types/server/tabular'

function joinPath(base: string, child: string): string {
	const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base
	const normalizedChild = child.startsWith('/') ? child.slice(1) : child
	return `${normalizedBase}/${normalizedChild}`
}

// Declaring props types for DataTable
interface Props {
	entriesPerPage?:
		| false
		| {
				defaultValue: number
				entries: number[]
		  }
	canSearch?: boolean
	showTotalEntries?: boolean
	table: {
		columns: Column[]
		rows: { [key: string]: any }[]
	}
	remoteSearch?: {
		value: string
		onChange: (value: string) => void
	}
	pagination?: {
		type: 'local' | 'remote'
		variant: 'contained' | 'gradient'
		color: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'dark' | 'light'
		remotePagination?: {
			fetchNextPage: (
				options?: FetchNextPageOptions
			) => Promise<InfiniteQueryObserverResult<TabularData, unknown>>
			hasNextPage: boolean
			currentPageIndex: number
			pageIndexes: number[]
			setCurrentPageIndex: Dispatch<SetStateAction<number>>
		}
	}
	filters?: FilterListValue
	remoteFilters?: {
		values: Record<string, string | null>
		onChange: (field: string, value: string | null) => void
	}
	initialFilter?: Record<string, string>
	isSorted?: boolean
	noEndBorder?: boolean
	parentPath?: string
	onRowClick?: (row: { [key: string]: any }) => void
}

function DataTable({
	entriesPerPage = { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
	canSearch = false,
	showTotalEntries = true,
	table,
	remoteSearch,
	pagination = { type: 'local', variant: 'gradient', color: 'primary' },
	isSorted = true,
	noEndBorder = false,
	parentPath,
	onRowClick,
	filters,
	remoteFilters,
	initialFilter,
}: Props): JSX.Element {
	let defaultValue: number
	let entries: number[]

	const [filtersValue, setFiltersValue] = useState<Record<string, string | null>>(() => {
		if (!filters) {
			return {}
		}
		const initial: Record<string, string | null> = {}
		filters.fields.forEach((key: string) => {
			if (initialFilter && initialFilter[key]) {
				initial[key] = initialFilter[key]
			} else {
				initial[key] = null
			}
		})
		return initial
	})

	const [localTable, setLocalTable] = useState(table)

	if (entriesPerPage) {
		defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10
		entries = entriesPerPage.entries ? entriesPerPage.entries : [10, 25, 50, 100]
	}
	const hasNextUiPage = pagination?.remotePagination?.pageIndexes?.includes(
		pagination.remotePagination.currentPageIndex + 1
	)

	const columns = useMemo<Column[]>(() => localTable.columns, [localTable])
	const data = useMemo(() => localTable.rows, [localTable])

	const tableInstance = useTable(
		{ columns, data, initialState: { pageIndex: 0 } },
		useGlobalFilter,
		useSortBy,
		usePagination
	)

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		rows,
		page,
		pageOptions,
		canPreviousPage,
		canNextPage,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		setGlobalFilter,
		state: { pageIndex, pageSize, globalFilter },
	}: TableInstance<object> = tableInstance

	// Set the default value for the entries per page when component mounts
	useEffect(() => setPageSize(defaultValue || 10), [defaultValue, setPageSize])

	useEffect(() => {
		setLocalTable(table)
	}, [table])

	// Set the entries per page value based on the select value
	const setEntriesPerPage = (value: any) => setPageSize(value)

	// Render the paginations
	const renderPagination = pageOptions.map((option: any) => (
		<MDPagination
			item
			key={option}
			onClick={() => gotoPage(Number(option))}
			active={pageIndex === option}
		>
			{option + 1}
		</MDPagination>
	))

	// Handler for the input to set the pagination index
	const handleInputPagination = ({ target: { value } }: any) =>
		value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value))

	// Customized page options starting from 1
	const customizedPageOptions = pageOptions.map((option: any) => option + 1)

	// Setting value for the pagination input
	const handleInputPaginationValue = ({ target: value }: any) => gotoPage(Number(value.value - 1))

	// Search input value state
	const [search, setSearch] = useState(globalFilter)

	// Search input state handle
	const onSearchChange = useAsyncDebounce((value: string) => {
		setGlobalFilter(value || undefined)
	}, 100)

	// A function that sets the sorted value for the table
	const setSortedValue = (column: any) => {
		let sortedValue

		if (isSorted && column.isSorted) {
			sortedValue = column.isSortedDesc ? 'desc' : 'asce'
		} else if (isSorted) {
			sortedValue = 'none'
		} else {
			sortedValue = false
		}

		return sortedValue
	}

	// Setting the entries starting point
	const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1

	// Setting the entries ending point
	let entriesEnd

	if (pageIndex === 0) {
		entriesEnd = pageSize
	} else if (pageIndex === pageOptions.length - 1) {
		entriesEnd = rows.length
	} else {
		entriesEnd = pageSize * (pageIndex + 1)
	}

	const handleFilterChange = (key: string, value: string | null) => {
		setFiltersValue(prev => ({
			...prev,
			[key]: value,
		}))
	}

	useEffect(() => {
		if (initialFilter && filters) {
			const filteredRows = table.rows.filter(row =>
				filters.fields.every(field => {
					const filterVal = initialFilter[field]
					return !filterVal || row[field] === filterVal
				})
			)

			setLocalTable({
				...table,
				rows: filteredRows,
			})
		}
	}, [initialFilter, filters, table])

	const filterTable = useCallback(
		(filterKey: string, newValue: string | null) => {
			if (!filters) {
				return table.rows
			}
			return table.rows.filter(row =>
				filters.fields.every(f => {
					const filterVal = f === filterKey ? newValue : filtersValue[f]
					return !filterVal || row[f] === filterVal
				})
			)
		},
		[table.rows, filtersValue, filters]
	)

	const isRemoteSearch = pagination.type === 'remote' && !!remoteSearch
	const isRemoteFilters = pagination.type === 'remote' && !!remoteFilters

	return (
		<TableContainer sx={{ boxShadow: 'none' }}>
			{entriesPerPage || canSearch ? (
				<MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
					{entriesPerPage && (
						<MDBox display="flex" alignItems="center" justifyContent={'flex-end'}>
							<Autocomplete
								disableClearable
								value={pageSize.toString()}
								options={entries.map(entry => entry.toString())}
								onChange={(event, newValue) => {
									setEntriesPerPage(newValue)
								}}
								size="small"
								sx={{ width: '5rem' }}
								renderInput={params => <MDInput {...params} />}
							/>
							<MDTypography variant="caption" color="secondary">
								&nbsp;&nbsp;entries per page
							</MDTypography>
						</MDBox>
					)}
					{canSearch && (
						<MDBox width="12rem" ml="auto">
							<MDInput
								placeholder="Search..."
								value={isRemoteSearch ? remoteSearch.value : search}
								size="small"
								fullWidth
								onChange={({ currentTarget }: any) => {
									if (isRemoteSearch) {
										remoteSearch.onChange(currentTarget.value)
										return
									}
									setSearch(currentTarget.value)
									onSearchChange(currentTarget.value)
								}}
							/>
						</MDBox>
					)}
				</MDBox>
			) : null}
			{filters && (
				<MDBox display={'flex'} flexDirection={'row'} alignItems={'center'}>
					<MDTypography variant="h6" color="text" ml={3} mb={2}>
						Filters:
					</MDTypography>
					<Grid
						container
						alignItems={'center'}
						justifyContent={'flex-start'}
						mb={2}
						ml={3}
						gap={12}
					>
						{filters.fields.map((field: string, key: number) => {
							return (
								<Grid item xs={6} sm={3} md={1} key={key}>
									<Autocomplete
										value={isRemoteFilters ? remoteFilters.values[field] : filtersValue[field]}
										options={filters.options[field]}
										onChange={(event, newValue) => {
											if (isRemoteFilters) {
												remoteFilters.onChange(field, newValue)
												return
											}
											handleFilterChange(field, newValue)
											const filteredRows = filterTable(field, newValue)
											setLocalTable({
												...table,
												rows: filteredRows,
											})
										}}
										size="small"
										sx={{ width: '10rem' }}
										renderInput={params => (
											<MDInput {...params} placeholder={field[0].toUpperCase() + field.slice(1)} />
										)}
									/>
								</Grid>
							)
						})}
					</Grid>
				</MDBox>
			)}
			<Table {...getTableProps()}>
				<MDBox component="thead">
					{headerGroups.map((headerGroup: any, key: any) => (
						<TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column: any, key: any) => (
								<DataTableHeadCell
									key={key}
									{...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
									width={column.width ? column.width : 'auto'}
									align={column.align ? column.align : 'left'}
									sorted={setSortedValue(column)}
								>
									{column.render('Header')}
								</DataTableHeadCell>
							))}
						</TableRow>
					))}
				</MDBox>
				<TableBody {...getTableBodyProps()}>
					{page.map((row: Row<object>, key: number) => {
						prepareRow(row)
						return (
							<TableRow key={key} {...row.getRowProps()}>
								{row.cells.map((cell: Cell<object, any>, key: number) => {
									const columnAccessor = cell.column.id.replace(/_/g, ' ').toLowerCase()
									const rowValue: any = cell.row.original
									const route = rowValue?.route
									const href = onRowClick
										? undefined
										: route && parentPath
										? joinPath(parentPath, route)
										: route
										? `${route}`
										: undefined

									let isSpecialColumn: SpecialColumn
									if (/\b(datetime|timestamp)\b/.test(columnAccessor)) {
										isSpecialColumn = 'DATETIME'
									} else if (/\bdate\b/.test(columnAccessor)) {
										isSpecialColumn = 'DATE'
									} else if (/\btime\b/.test(columnAccessor)) {
										isSpecialColumn = 'TIME'
									} else if (/\bid\b/.test(columnAccessor) && cell.value.length > 20) {
										isSpecialColumn = 'LONG_ID'
									}
									return (
										<DataTableBodyCell
											key={key}
											route={href}
											onClick={onRowClick ? () => onRowClick(rowValue) : undefined}
											noBorder={noEndBorder && rows.length - 1 === key}
											{...cell.getCellProps()}
										>
											<MDBox color="text" sx={{ width: '100%' }}>
												{isSpecialColumn === 'DATE'
													? new Date(cell.value).toLocaleDateString(undefined, {
															year: '2-digit',
															month: '2-digit',
															day: '2-digit',
													  })
													: isSpecialColumn === 'TIME'
													? new Date(cell.value).toLocaleTimeString(undefined, {
															hour: '2-digit',
															minute: '2-digit',
													  })
													: isSpecialColumn === 'DATETIME'
													? new Date(cell.value).toLocaleString(undefined, {
															year: '2-digit',
															month: '2-digit',
															day: '2-digit',
															hour: '2-digit',
															minute: '2-digit',
													  })
													: isSpecialColumn === 'LONG_ID' &&
													  typeof cell.value === 'string' &&
													  cell.value.length > 20
													? cell.value.slice(0, 20) + '...'
													: cell.render('Cell')}
											</MDBox>
										</DataTableBodyCell>
									)
								})}
							</TableRow>
						)
					})}
				</TableBody>
			</Table>

			<MDBox
				display="flex"
				flexDirection={{ xs: 'column', sm: 'row' }}
				justifyContent="space-between"
				alignItems={{ xs: 'flex-start', sm: 'center' }}
				p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
			>
				{showTotalEntries && (
					<MDBox mb={{ xs: 3, sm: 0 }}>
						<MDTypography variant="button" color="secondary" fontWeight="regular">
							{rows.length > 0
								? `Showing ${entriesStart} to ${entriesEnd} of ${rows.length} entries`
								: 'No Data Available'}
						</MDTypography>
					</MDBox>
				)}
				{pagination.type === 'local' ? (
					pageOptions.length > 1 && (
						<MDPagination
							variant={pagination.variant ? pagination.variant : 'gradient'}
							color={pagination.color ? pagination.color : 'primary'}
						>
							{canPreviousPage && (
								<MDPagination item onClick={() => previousPage()}>
									<Icon sx={{ fontWeight: 'bold' }}>chevron_left</Icon>
								</MDPagination>
							)}
							{renderPagination.length > 6 ? (
								<MDBox width="5rem" mx={1}>
									<MDInput
										inputProps={{
											type: 'number',
											min: 1,
											max: customizedPageOptions.length,
											readOnly: true,
										}}
										value={customizedPageOptions[pageIndex]}
										onChange={(event: any) => {
											handleInputPagination(event)
											handleInputPaginationValue(event)
										}}
									/>
								</MDBox>
							) : (
								renderPagination
							)}
							{canNextPage && (
								<MDPagination item onClick={() => nextPage()}>
									<Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
								</MDPagination>
							)}
						</MDPagination>
					)
				) : (
					<MDPagination
						variant={pagination.variant ? pagination.variant : 'gradient'}
						color={pagination.color ? pagination.color : 'primary'}
					>
						{pagination.remotePagination?.currentPageIndex >= 1 && (
							<MDPagination
								item
								onClick={async () => {
									pagination.remotePagination.setCurrentPageIndex(prev => prev - 1)
								}}
							>
								<Icon sx={{ fontWeight: 'bold' }}>chevron_left</Icon>
							</MDPagination>
						)}
						<MDPagination item active={true}>
							{pagination.remotePagination?.currentPageIndex + 1}
						</MDPagination>
						{(pagination.remotePagination?.hasNextPage || hasNextUiPage) && (
							<MDPagination
								item
								onClick={async () => {
									if (
										pagination.remotePagination.currentPageIndex ===
										pagination.remotePagination.pageIndexes.length - 1
									) {
										await pagination.remotePagination?.fetchNextPage()
									}
									pagination.remotePagination.setCurrentPageIndex(prev => prev + 1)
								}}
							>
								<Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
							</MDPagination>
						)}
					</MDPagination>
				)}
			</MDBox>
		</TableContainer>
	)
}

export default DataTable
